import { Injectable, inject } from '@angular/core';
import { ApiDataService } from '../http-data/apidata.service';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/redux/app.state';
import { Observable } from 'rxjs';
import { IActionResult } from '../http-data/apidata.model';
import { ITemplateInstallationRequest } from 'src/app/models/template-installation-requests.model';
import { environment } from 'src/environments/environment';
import { templateInstallationRequests } from 'src/app/redux/selectors/template-installation-requests.selector';
import { addTemplateInstallationRequests, loadTemplateInstallationRequests, upsertTemplateInstallationRequest, upsertTemplateInstallationRequestActivity, upsertTemplateInstallationRequests, upsertWorkflowExecutionStatus } from 'src/app/redux/actions/templates/template-installation-requests.actions';
import { WorkflowExecutionStatus } from 'src/app/models/workflow-request.model';
import { ActivityLog } from 'src/app/models/activity-logs/activity-logs.model';

@Injectable({
  providedIn: 'root'
})
export class TemplateInstallationRequestsService {
  private apiDataService = inject(ApiDataService);
  private store = inject<Store<AppState>>(Store);


  /**
     * Load template installation requests form the API
     * ! DO NOT CALL DIRECTLY - ONLY USED IN template-installation-requests.effects.ts - USE getTemplateInstallationRequests() from this service
     * @returns List of requests
     */
  public loadTemplateInstallationRequests(): Observable<IActionResult<ITemplateInstallationRequest[]>> {
    return this.apiDataService.get<ITemplateInstallationRequest[]>(`${environment.urls.hubApi}api/templates/requests`);
  }

  // Get the template installatio requests in the Store or trigger the loading process if they are not loaded
  public getTemplateInstallationRequests(): Promise<ITemplateInstallationRequest[]> {
    return new Promise<ITemplateInstallationRequest[]>((resolve, reject) => {
      let resolved = false
      let ranOnce = false
      const storeSubscription = this.redux_getTemplateInstallationRequestsSelector().subscribe({
        next: (templateInstallationRequestsState) => {
          if (!templateInstallationRequestsState) {
            reject();
          } else {
            if (!templateInstallationRequestsState.loading) {
              if (templateInstallationRequestsState.error) {
                reject();

                resolved = true;
                // If has already ran once then unsbcribe
                if (ranOnce) {
                  storeSubscription.unsubscribe();
                }
              } else if (!templateInstallationRequestsState.loadedOnce) {
                this.redux_loadTemplateInstallationRequests()
              } else {                
                resolve(templateInstallationRequestsState.value);

                resolved = true;
                // If has already ran once then unsbcribe
                if (ranOnce) {
                  storeSubscription.unsubscribe();
                }
              }
            }
          }
        }
      });

      ranOnce = true;
      // If has values on first run then unsbcribe
      if (resolved) {
        storeSubscription.unsubscribe();
      }
    });
  }

  public getTemplateInstallationCurrentActivity(): Promise<ITemplateInstallationRequest[]> {
    return new Promise<ITemplateInstallationRequest[]>((resolve) => {
      this.apiDataService.get<ITemplateInstallationRequest[]>(`${environment.urls.hubApi}api/templates/requests/current`).subscribe({
        next: async (resp) => {
          if (resp.data) {
            this.store.dispatch(addTemplateInstallationRequests({ requests: resp.data }))
          }
          resolve(resp.data)
        },
        error: () => {
          resolve([])
        }
      })
    })
  }

  // Get a workflow by id
  public async getInstallationRequestByTaskId(taskId: string): Promise<ITemplateInstallationRequest | undefined> {
    return (await this.getTemplateInstallationRequests())?.find(x => x.taskId === taskId)
  }

  //#region REDUX OPERATIONS

  private redux_loadTemplateInstallationRequests() {
    this.store.dispatch(loadTemplateInstallationRequests())
  }


  public redux_getTemplateInstallationRequestsSelector() {
    return this.store.select(templateInstallationRequests)
  }

  public redux_upsertInstallationRequest(request: ITemplateInstallationRequest) {
    this.store.dispatch(upsertTemplateInstallationRequest({ request }))
  }

  public redux_upsertInstallationRequests(requests: ITemplateInstallationRequest[]) {
    this.store.dispatch(upsertTemplateInstallationRequests({ requests }))
  }

  public redux_updateExecutionStatusWorkflow(taskId: string, executionStatus: WorkflowExecutionStatus) {
		this.store.dispatch(upsertWorkflowExecutionStatus({ taskId, executionStatus }))
	}

  public redux_updateInstallationsActivity(activity: ActivityLog[]){
    this.store.dispatch(upsertTemplateInstallationRequestActivity({ activity }))
  }

  public redux_getRequestSelector(taskId: string){
		return this.store.select(state => state.templateInstallationRequests.value.find(request => 
			request.taskId == taskId
		))
	}
  //#endregion

}
